import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgCommonModule, NgDialogModule } from '@principle-theorem/ng-shared';
import {
  IMedia,
  IPatient,
  IReferralMedia,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  multiUndeleted,
  query$,
  WithRef,
} from '@principle-theorem/shared';
import { NgMediaManagerModule } from '../../ng-media-manager.module';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { Patient } from '@principle-theorem/principle-core';

export interface IPatientFileSelectionDialogData {
  patient: WithRef<IPatient>;
  selectedMedia?: WithRef<IReferralMedia>[];
}

@Component({
  selector: 'pr-patient-file-selection-dialog',
  imports: [NgDialogModule, NgCommonModule, NgMediaManagerModule],
  templateUrl: './patient-file-selection-dialog.component.html',
  styleUrl: './patient-file-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientFileSelectionDialogComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<PatientFileSelectionDialogComponent>
  );

  readonly data = inject<IPatientFileSelectionDialogData>(MAT_DIALOG_DATA);
  readonly initialMedia = this.data.selectedMedia || [];

  media = toSignal(this._getPatientMedia$(this.data.patient), {
    initialValue: [],
  });

  readonly selectedMediaItems = signal<WithRef<IMedia>[]>(
    this.initialMedia.map((referralMedia) => {
      if (referralMedia.patientMediaRef) {
        const matchingMedia = this.media().find((media) =>
          isSameRef(media.ref, referralMedia.patientMediaRef)
        );
        return matchingMedia || (referralMedia as unknown as WithRef<IMedia>);
      }

      return referralMedia as unknown as WithRef<IMedia>;
    })
  );

  readonly hasChanges = computed(() => {
    const currentSelection = this.selectedMediaItems();
    const initialSelection = this.initialMedia;

    if (initialSelection.length !== currentSelection.length) {
      return true;
    }

    const initialIds = new Set(initialSelection.map((item) => item.ref.id));
    return currentSelection.some((item) => !initialIds.has(item.ref.id));
  });

  addFiles(): void {
    this._dialogRef.close(this.selectedMediaItems());
  }

  mediaSelected(media: WithRef<IMedia>[]): void {
    this.selectedMediaItems.set(media);
  }

  private _getPatientMedia$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IMedia>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      (patientReffable) =>
        query$(Patient.mediaCol(patientReffable)).pipe(multiUndeleted())
    );
  }
}
